// src/offline.ts
import type { OutgoingPayload } from './types';

type StoredItem = {
    ts: number;
    payload: OutgoingPayload;
};

type OfflineOptions = {
    enabled: boolean;
    maxItems: number;
    maxAgeSec: number;
};

const DB_NAME = 'poulet_metrics';
const STORE_NAME = 'queue';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase | null> | null = null;

const getDb = () => {
    if (typeof indexedDB === 'undefined') return Promise.resolve(null);
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { autoIncrement: true });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
    });
    return dbPromise;
};

const withStore = async <T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>) => {
    const db = await getDb();
    if (!db) return undefined;
    return await new Promise<T | undefined>((resolve) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        const req = run(store);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(undefined);
    });
};

const pruneByAge = async (cutoffMs: number) => {
    const db = await getDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
            const cursor = cursorReq.result as IDBCursorWithValue | null;
            if (!cursor) return resolve();
            const value = cursor.value as StoredItem;
            if (value.ts < cutoffMs) {
                cursor.delete();
            }
            cursor.continue();
        };
        cursorReq.onerror = () => resolve();
    });
};

const pruneByCount = async (maxItems: number) => {
    if (maxItems <= 0) return;
    const count = await withStore('readonly', (store) => store.count());
    if (!count || count <= maxItems) return;
    const toDelete = count - maxItems;
    const db = await getDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        let removed = 0;
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
            const cursor = cursorReq.result as IDBCursorWithValue | null;
            if (!cursor || removed >= toDelete) return resolve();
            cursor.delete();
            removed += 1;
            cursor.continue();
        };
        cursorReq.onerror = () => resolve();
    });
};

export const saveOfflineItem = async (payload: OutgoingPayload, options: OfflineOptions) => {
    if (!options.enabled) return undefined;
    const key = await withStore('readwrite', (store) => store.add({ ts: Date.now(), payload } as StoredItem));
    const cutoff = Date.now() - options.maxAgeSec * 1000;
    await pruneByAge(cutoff);
    await pruneByCount(options.maxItems);
    return typeof key === 'number' ? key : undefined;
};

export const loadOfflineBatch = async (limit: number, options: OfflineOptions) => {
    if (!options.enabled) return [];
    const cutoff = Date.now() - options.maxAgeSec * 1000;
    await pruneByAge(cutoff);
    const db = await getDb();
    if (!db) return [];
    return await new Promise<Array<{ key: number; payload: OutgoingPayload }>>((resolve) => {
        const out: Array<{ key: number; payload: OutgoingPayload }> = [];
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
            const cursor = cursorReq.result as IDBCursorWithValue | null;
            if (!cursor || out.length >= limit) return resolve(out);
            const value = cursor.value as StoredItem;
            out.push({ key: cursor.key as number, payload: value.payload });
            cursor.continue();
        };
        cursorReq.onerror = () => resolve(out);
    });
};

export const deleteOfflineItems = async (keys: number[]) => {
    if (!keys.length) return;
    const db = await getDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        keys.forEach((key) => store.delete(key));
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
    });
};

export type { OfflineOptions };
